import { ElementType, ReactElement, ReactNode } from 'react'
import { Box, SvgIconProps, useTheme } from '@mui/material'

export type TabsItem =
  | string
  | {
      id: string
      label?: string
      icon?: ElementType<SvgIconProps>
      render?: (active: boolean) => ReactNode
    }

export type TabsProps = {
  items: TabsItem[]
  value: string
  onChange?: (id: string) => void
  size?: 'sm' | 'md'
  variant?: 'text' | 'icon'
  kind?: 'underline' | 'segmented' | 'pills' | 'folder' | 'minimal' | 'block'
}

// =============================================================================
// Tabs — multi-style tab strip
//
// Six visual styles, swappable via the `kind` prop:
//   underline · segmented · pills · folder · minimal · block
//
// Each item can be a plain string ("Bemessung") or an object
//   { id, label, icon, render } — icon/render only used by variant="icon".
//
// Props
//   items     Array of strings or { id, label } objects
//   value     id of the currently active tab
//   onChange  (id) => void
//   size      "sm" | "md"  (default "md")
//   variant   "text" | "icon"  (default "text") — icon mode forces underline
//   kind      "underline" | "segmented" | "pills" | "folder" | "minimal" | "block"
//
// Depends on CSS vars:
//   --mui-divider, --mui-primary-main, --fg1, --fg2, --fg3
// =============================================================================
const Tabs = ({
  items,
  value,
  onChange,
  size = 'md',
  variant = 'text',
  kind = 'underline',
}: TabsProps): ReactElement => {
  // Sub/icon tabs always use underline regardless of `kind`.
  const effectiveKind = variant === 'icon' ? 'underline' : kind

  if (effectiveKind === 'segmented')
    return <SegmentedTabs items={items} value={value} onChange={onChange} size={size} />
  if (effectiveKind === 'pills')
    return <PillTabs items={items} value={value} onChange={onChange} size={size} />
  if (effectiveKind === 'folder')
    return <FolderTabs items={items} value={value} onChange={onChange} size={size} />
  if (effectiveKind === 'minimal')
    return <MinimalTabs items={items} value={value} onChange={onChange} size={size} />
  if (effectiveKind === 'block')
    return <BlockTabs items={items} value={value} onChange={onChange} size={size} />
  return (
    <UnderlineTabs items={items} value={value} onChange={onChange} size={size} variant={variant} />
  )
}

const asObject = (item: TabsItem): Exclude<TabsItem, string> =>
  typeof item === 'string' ? { id: item, label: item } : item

const CssVars = ({ children }: { children: ReactNode }): ReactElement => {
  const theme = useTheme()
  return (
    <Box
      style={
        {
          '--mui-divider': theme.palette.divider,
          '--mui-primary-main': theme.palette.primary.main,
          '--fg1': theme.palette.text.primary,
          '--fg2': theme.palette.text.secondary,
          '--fg3': theme.palette.text.disabled,
        } as React.CSSProperties
      }
    >
      {children}
    </Box>
  )
}

// --- Underline (MUI default) -----------------------------------------------
const UnderlineTabs = ({
  items,
  value,
  onChange,
  size,
  variant,
}: {
  items: TabsItem[]
  value: string
  onChange?: (id: string) => void
  size: 'sm' | 'md'
  variant: 'text' | 'icon'
}): ReactElement => (
  <CssVars>
    <div
      style={{
        display: 'flex',
        borderBottom: '1px solid var(--mui-divider)',
        gap: 0,
        height: size === 'sm' ? 36 : 44,
        alignItems: 'stretch',
      }}
    >
      {items.map(item => {
        const obj = asObject(item)
        const active = obj.id === value
        return (
          <button
            type="button"
            key={obj.id}
            title={obj.label || obj.id}
            aria-label={obj.label || obj.id}
            onClick={() => onChange && onChange(obj.id)}
            style={{
              appearance: 'none',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              flex: variant === 'icon' ? 1 : 'none',
              padding: variant === 'icon' ? 0 : size === 'sm' ? '0 12px' : '0 16px',
              fontSize: size === 'sm' ? 13 : 14,
              fontWeight: 500,
              textTransform: variant === 'icon' || size === 'sm' ? 'none' : 'uppercase',
              letterSpacing: variant === 'icon' || size === 'sm' ? 0 : '0.02857em',
              color: active ? 'var(--mui-primary-main)' : 'var(--fg2)',
              borderBottom: `2px solid ${active ? 'var(--mui-primary-main)' : 'transparent'}`,
              marginBottom: -1,
              cursor: 'pointer',
              fontFamily: 'Roboto',
              transition: 'color 150ms, background 120ms',
              border: 0,
              background: 'transparent',
            }}
            onMouseEnter={e => {
              if (!active) e.currentTarget.style.background = 'rgba(0,0,0,0.04)'
            }}
            onMouseLeave={e => {
              e.currentTarget.style.background = 'transparent'
            }}
          >
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                pointerEvents: 'none',
              }}
            >
              {variant === 'icon' && obj.render ? (
                obj.render(active)
              ) : variant === 'icon' && obj.icon ? (
                <obj.icon sx={{ fontSize: size === 'sm' ? 18 : 20 }} />
              ) : (
                obj.label || obj.id
              )}
            </span>
          </button>
        )
      })}
    </div>
  </CssVars>
)

// --- Segmented (iOS / SwiftUI segmented control) ---------------------------
const SegmentedTabs = ({
  items,
  value,
  onChange,
  size,
}: {
  items: TabsItem[]
  value: string
  onChange?: (id: string) => void
  size: 'sm' | 'md'
}): ReactElement => {
  const arr = items.map(asObject)
  return (
    <CssVars>
      <div
        style={{
          padding: size === 'sm' ? '8px 12px' : '10px 14px',
          borderBottom: '1px solid var(--mui-divider)',
          background: 'transparent',
        }}
      >
        <div
          style={{
            display: 'inline-flex',
            background: 'rgba(0,0,0,0.05)',
            borderRadius: 8,
            padding: 3,
            gap: 2,
          }}
        >
          {arr.map(obj => {
            const active = obj.id === value
            return (
              <button
                type="button"
                key={obj.id}
                onClick={() => onChange && onChange(obj.id)}
                style={{
                  appearance: 'none',
                  border: 0,
                  cursor: 'pointer',
                  padding: size === 'sm' ? '5px 12px' : '7px 16px',
                  fontSize: 13,
                  fontWeight: active ? 600 : 500,
                  fontFamily: 'Roboto',
                  color: active ? 'var(--fg1)' : 'var(--fg2)',
                  background: active ? '#fff' : 'transparent',
                  borderRadius: 6,
                  boxShadow: active
                    ? '0 1px 2px rgba(0,0,0,0.10), 0 0 0 0.5px rgba(0,0,0,0.04)'
                    : 'none',
                  transition: 'background 150ms, color 120ms, box-shadow 120ms',
                }}
              >
                {obj.label || obj.id}
              </button>
            )
          })}
        </div>
      </div>
    </CssVars>
  )
}

// --- Pills (rounded chips) -------------------------------------------------
const PillTabs = ({
  items,
  value,
  onChange,
  size,
}: {
  items: TabsItem[]
  value: string
  onChange?: (id: string) => void
  size: 'sm' | 'md'
}): ReactElement => {
  const arr = items.map(asObject)
  return (
    <CssVars>
      <div
        style={{
          display: 'flex',
          gap: 6,
          alignItems: 'center',
          padding: size === 'sm' ? '8px 12px' : '10px 14px',
          borderBottom: '1px solid var(--mui-divider)',
        }}
      >
        {arr.map(obj => {
          const active = obj.id === value
          return (
            <button
              type="button"
              key={obj.id}
              onClick={() => onChange && onChange(obj.id)}
              style={{
                appearance: 'none',
                cursor: 'pointer',
                padding: size === 'sm' ? '5px 12px' : '6px 14px',
                fontSize: 13,
                fontWeight: 500,
                fontFamily: 'Roboto',
                color: active ? 'var(--mui-primary-main)' : 'var(--fg2)',
                background: active ? 'rgba(55,132,52,0.10)' : 'transparent',
                border: `1px solid ${active ? 'rgba(55,132,52,0.35)' : 'rgba(0,0,0,0.12)'}`,
                borderRadius: 999,
                transition: 'all 120ms',
              }}
              onMouseEnter={e => {
                if (!active) e.currentTarget.style.background = 'rgba(0,0,0,0.04)'
              }}
              onMouseLeave={e => {
                if (!active) e.currentTarget.style.background = 'transparent'
              }}
            >
              {obj.label || obj.id}
            </button>
          )
        })}
      </div>
    </CssVars>
  )
}

// --- Folder (browser-tab look) ---------------------------------------------
const FolderTabs = ({
  items,
  value,
  onChange,
  size,
}: {
  items: TabsItem[]
  value: string
  onChange?: (id: string) => void
  size: 'sm' | 'md'
}): ReactElement => {
  const arr = items.map(asObject)
  return (
    <CssVars>
      <div
        style={{
          display: 'flex',
          alignItems: 'stretch',
          gap: 2,
          height: size === 'sm' ? 34 : 36,
          padding: size === 'sm' ? '2px 8px 0' : '3px 8px 0',
          boxSizing: 'border-box',
          background: 'rgba(0,0,0,0.03)',
          borderBottom: '1px solid var(--mui-divider)',
        }}
      >
        {arr.map(obj => {
          const active = obj.id === value
          return (
            <button
              type="button"
              key={obj.id}
              onClick={() => onChange && onChange(obj.id)}
              style={{
                appearance: 'none',
                cursor: 'pointer',
                display: 'inline-flex',
                justifyContent: 'center',
                alignItems: active ? 'flex-start' : 'center',
                // Keep a stable height to avoid vertical "jump" during transitions.
                height: size === 'sm' ? 33 : 34,
                padding: size === 'sm' ? '0 14px' : '0 16px',
                paddingTop: active ? (size === 'sm' ? 6 : 7) : 0,
                marginTop: active ? (size === 'sm' ? 2 : 3) : 0,
                marginBottom: -1,
                fontFamily: 'Roboto, system-ui, sans-serif',
                // Keep font size stable; only style changes should animate.
                fontSize: size === 'sm' ? 12.5 : 13,
                fontWeight: active ? 600 : 500,
                lineHeight: 1.1,
                textTransform: 'none',
                letterSpacing: active ? '-0.005em' : '0.02em',
                fontFeatureSettings: '"ss01", "cv11"',
                color: active ? 'var(--fg2)' : 'var(--fg3)',
                background: active ? '#fff' : 'transparent',
                border: active ? '1px solid var(--mui-divider)' : '1px solid transparent',
                borderBottomColor: active ? '#fff' : 'transparent',
                borderTopLeftRadius: 8,
                borderTopRightRadius: 8,
                transition: 'background 120ms, color 120ms, border-color 120ms',
                alignSelf: 'stretch',
              }}
              onMouseEnter={e => {
                if (!active) {
                  e.currentTarget.style.background = 'rgba(0,0,0,0.04)'
                  e.currentTarget.style.color = 'var(--fg2)'
                }
              }}
              onMouseLeave={e => {
                if (!active) {
                  e.currentTarget.style.background = 'transparent'
                  e.currentTarget.style.color = 'var(--fg3)'
                }
              }}
            >
              {obj.label || obj.id}
            </button>
          )
        })}
      </div>
    </CssVars>
  )
}

// --- Minimal (text + tiny dot) ---------------------------------------------
const MinimalTabs = ({
  items,
  value,
  onChange,
  size,
}: {
  items: TabsItem[]
  value: string
  onChange?: (id: string) => void
  size: 'sm' | 'md'
}): ReactElement => {
  const arr = items.map(asObject)
  return (
    <CssVars>
      <div
        style={{
          display: 'flex',
          gap: 22,
          alignItems: 'center',
          padding: size === 'sm' ? '10px 16px' : '12px 18px',
          borderBottom: '1px solid var(--mui-divider)',
        }}
      >
        {arr.map(obj => {
          const active = obj.id === value
          return (
            <button
              type="button"
              key={obj.id}
              onClick={() => onChange && onChange(obj.id)}
              style={{
                appearance: 'none',
                border: 0,
                background: 'transparent',
                cursor: 'pointer',
                padding: 0,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 7,
                fontSize: 13,
                fontWeight: active ? 600 : 500,
                fontFamily: 'Roboto',
                color: active ? 'var(--fg1)' : 'var(--fg3)',
              }}
            >
              <span
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: 999,
                  background: active ? 'var(--mui-primary-main)' : 'transparent',
                  border: active ? 'none' : '1px solid rgba(0,0,0,0.18)',
                  transition: 'background 120ms',
                }}
              />
              {obj.label || obj.id}
            </button>
          )
        })}
      </div>
    </CssVars>
  )
}

// --- Block (full-width filled cells) ---------------------------------------
const BlockTabs = ({
  items,
  value,
  onChange,
  size,
}: {
  items: TabsItem[]
  value: string
  onChange?: (id: string) => void
  size: 'sm' | 'md'
}): ReactElement => {
  const arr = items.map(asObject)
  return (
    <CssVars>
      <div
        style={{
          display: 'flex',
          borderBottom: '1px solid var(--mui-divider)',
          background: 'rgba(0,0,0,0.025)',
        }}
      >
        {arr.map(obj => {
          const active = obj.id === value
          return (
            <button
              type="button"
              key={obj.id}
              onClick={() => onChange && onChange(obj.id)}
              style={{
                appearance: 'none',
                border: 0,
                cursor: 'pointer',
                flex: 1,
                padding: size === 'sm' ? '10px 12px' : '13px 16px',
                fontSize: 13,
                fontWeight: active ? 600 : 500,
                fontFamily: 'Roboto',
                color: active ? 'var(--mui-primary-main)' : 'var(--fg2)',
                background: active ? '#fff' : 'transparent',
                borderRight: '1px solid var(--mui-divider)',
                boxShadow: active ? 'inset 0 -2px 0 var(--mui-primary-main)' : 'none',
                transition: 'background 120ms, color 120ms',
              }}
              onMouseEnter={e => {
                if (!active) e.currentTarget.style.background = 'rgba(0,0,0,0.03)'
              }}
              onMouseLeave={e => {
                if (!active) e.currentTarget.style.background = 'transparent'
              }}
            >
              {obj.label || obj.id}
            </button>
          )
        })}
      </div>
    </CssVars>
  )
}

export default Tabs
